// src/pages/ExternalExpertApplicationsInExpertise.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from '../api/axios';
import ExternalExpertScoringPage from './ExternalExpertScoringPage';
import ApplicationDetailsModal from './modals/ApplicationDetailsModal';
import SignatureApplicationModal from '../pages/modals/SignatureApplicationModal';
import { useNavigate, useLocation } from 'react-router-dom';

// одинаковые стили с GroupedApplicationsExternalExpert
import './GroupedApplicationsExternalExpert.css';
import './ExternalExpertApplicationsList.css';

/* ================= Helpers ================= */
const safeDate = (s) => (s ? new Date(String(s).replace(' ', 'T')) : null);

function formatTimeLeft(deadlineAt, locale = 'kz') {
  const L =
    locale === 'ru'
      ? { d: 'день', h: 'ч', m: 'м', overdue: 'Просрочено' }
      : { d: 'күн', h: 'с', m: 'м', overdue: 'Мерзімі өтті' };

  const d = safeDate(deadlineAt);
  if (!d) return '—';

  const diffMs = d - new Date();
  if (diffMs <= 0) return L.overdue;

  const totalMin = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;

  const parts = [];
  if (days) parts.push(`${days}${L.d}`);
  if (hours) parts.push(`${hours}${L.h}`);
  if (mins || parts.length === 0) parts.push(`${mins}${L.m}`);
  return parts.join(' ');
}

const minutesUntil = (deadlineAt) => {
  const d = safeDate(deadlineAt);
  if (!d) return null;
  return Math.floor((d - new Date()) / 60000);
};
const getTimeSeverity = (deadlineAt) => {
  const mins = minutesUntil(deadlineAt);
  if (mins == null) return 'ok';
  if (mins <= 0) return 'danger';
  if (mins <= 60) return 'danger';
  if (mins <= 24 * 60) return 'warn';
  return 'ok';
};

function normalizeStatus(statusRaw = '') {
  const s = String(statusRaw).toLowerCase();
  if (s.includes('сараптамада')) return 'errors_done';
  if (s.includes('қабылдады') || s.includes('принято')) return 'accepted';
  if (s.includes('қателерді анықтау') || s.includes('выявление ошибок')) return 'errors_detect';
  if (s.includes('қате анықтау аяқталды') || s.includes('ошибки завершены')) return 'errors_done';
  if (s.includes('бағалау аяқталды') || s.includes('оценка завершена')) return 'decision';
  if (s.includes('бағалау') || s.includes('оценк')) return 'scoring';
  if (s.includes('қорытындылау') || s.includes('подведение итогов')) return 'summary';
  if (s.includes('аяқталды') || s.includes('завершено') || s === 'бекітілді' || s.includes('утверждено')) return 'finished';
  return 'sent';
}

function mapApiToGroups(apiData, locale = 'kz') {
  return (apiData || []).map((group) => {
    const group_title = group.title || group.group_title || '—';
    const group_id = group.id ?? group.group_id ?? `g_${Math.random().toString(36).slice(2, 8)}`;

    const applications = (group.applications || []).map((app) => {
      const statusCode = normalizeStatus(app.status);

      // активный дедлайн по стадии
      let activeDeadline = app.deadline_at;
      if (statusCode === 'errors_detect')
        activeDeadline = app.errors_detect_deadline?.deadline_at || app.deadline_at;
      if (statusCode === 'scoring')
        activeDeadline = app.scores_eval_deadline?.deadline_at || app.deadline_at;

      const subject =
        (locale === 'ru'
          ? app.subject_ru ?? group.education_level?.name_ru
          : app.subject_kz ?? group.education_level?.name_kz) ??
        app.subject ?? // если приходит одно поле без суффикса
        group.education_level?.name_ru ??
        group.education_level?.name_kz ??
        '—';

      return {
        id: app.id,
        title: app.title,
        subject,
        category: app.category || '—',
        grade: app.grade || '—',
        publisher: app.publisher || group.publisher_name || '—',
        status: app.status,
        status_me: statusCode,
        _deadline_raw: activeDeadline,
        deadline_text: formatTimeLeft(activeDeadline, locale),
        continuation_mode: group.continuation_mode ?? (group.is_continuation ? 'continuation' : 'new'),
      };
    });

    return { group_id, group_title, applications, _raw: group };
  });
}

/* ========= Прогресс-бар (полная ширина) ========= */
const STATUS_STEPS = ['sent', 'accepted', 'errors_detect', 'errors_done', 'scoring', 'decision', 'summary', 'finished']; // 🆕

const STATUS_LABELS = {
  kz: {
    sent: 'Күтуде',
    accepted: 'Қабылданды',
    errors_detect: 'Қателерді анықтау',
    errors_done: 'Қателер аяқталды',
    scoring: 'Бағалау',
    decision: 'Шешім шығару',
    summary: 'Қорытындылау',
    finished: 'Аяқталды',
  },
  ru: {
    sent: 'В ожидании',
    accepted: 'Принято',
    errors_detect: 'Выявление ошибок',
    errors_done: 'Ошибки завершены',
    scoring: 'Оценка',
    decision: 'Принятие решения',
    summary: 'Подведение итогов',
    finished: 'Завершено',
  },
};


function InlineStatusProgressBar({ status, locale = 'kz' }) {
  const currentIndex = Math.max(0, STATUS_STEPS.indexOf(status));
  const L = STATUS_LABELS[locale] || STATUS_LABELS.kz;

  return (
    <div className="gai-statusbar">
      {STATUS_STEPS.map((code, i) => {
        const cls = i < currentIndex ? 'is-done' : i === currentIndex ? 'is-current' : 'is-next';
        return (
          <div key={code} className={`gai-statusstep ${cls}`}>
            <div className="gai-statusstep__track" />
            <div className="gai-statusstep__dot" />
            <span className="gai-statusstep__label">{L[code]}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ================= Component ================= */
export default function ExternalExpertApplicationsInExpertise({ locale = 'kz' }) {
  const navigate = useNavigate();
  const location = useLocation();

  // прочитаем page и q из URL ДО любых useState, которые будут это использовать
  const urlParams = new URLSearchParams(location.search);
  const initPage = Math.max(1, parseInt(urlParams.get('page') || '1', 10));
  const initQuery = urlParams.get('q') || '';

  // --- state ---
  const [groups, setGroups] = useState([]);
  const [finalAverage, setFinalAverage] = useState(null);
  const [summaryDecision, setSummaryDecision] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationViewMode, setApplicationViewMode] = useState(null);
  const [errorReviewData, setErrorReviewData] = useState(null);
  const [userIsScientist, setUserIsScientist] = useState(false);

  const [continuationFilter, setContinuationFilter] = useState('all');

  const [user, setUser] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  const [groupFilter, setGroupFilter] = useState(initQuery);
  const [base64File, setBase64File] = useState('');

  const [currentPage, setCurrentPage] = useState(initPage);
  const itemsPerPage = 4;

  // аккордеон
  const [expanded, setExpanded] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('eei_expanded_groups') || '{}');
    } catch {
      return {};
    }
  });
  const toggleGroup = useCallback((id) => setExpanded((p) => ({ ...p, [id]: !p[id] })), []);

  // сохранить локаль для дочерних страниц
  useEffect(() => {
    localStorage.setItem('locale', (locale || 'kz').toLowerCase());
  }, [locale]);

  // сохранить любое изменение expanded
  useEffect(() => {
    localStorage.setItem('eei_expanded_groups', JSON.stringify(expanded));
  }, [expanded]);

  // при обновлении списка групп — не терять предыдущее состояние
  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        if (typeof next[g.group_id] === 'undefined') next[g.group_id] = false;
      });
      return next;
    });
  }, [groups]);

  // мягко восстановим позицию скролла после возврата со страницы просмотра
  useEffect(() => {
    const y = parseInt(sessionStorage.getItem('ee_scroll_y') || '0', 10);
    if (y > 0) {
      setTimeout(() => {
        window.scrollTo({ top: y, behavior: 'auto' });
        sessionStorage.removeItem('ee_scroll_y');
      }, 0);
    }
  }, []);

  const token = localStorage.getItem('token');

  const t = useMemo(
    () =>
      ({
        kz: {
          loading: 'Жүктелуде...',
          noApplications: 'Өтінімдер табылмады',
          view: 'Толық көру',
          back: 'Артқа қайту',
          deadline: 'Дедлайн',
          invalidApplication: 'Бұл өтініш қазір қарастыру үшін жарамайды.',
          upload: 'Файл жүктеу',
          finish: 'Қателерді анықтауды аяқтау',
          secondaryAction: 'Шешім шығару',
          shortView: 'Қортындылау',
          signErrorPrefix: '❌ Қате',
          errorGeneric: 'Қате орын алды',
          generateReport: 'Құжатты генерациялау',
          searchByGroup: 'Топ атауы бойынша іздеу',
          signedSuccessfully: '✅ Қол қойылды!',
          finalFilePrefix: 'қорытынды',
          fileUploadedDraft: 'Файл жоба күйінде сақталды!',
          fileUploadError: 'Файлды жүктеу мүмкін болмады.',
          finishSuccess: 'Қателерді анықтау кезеңі аяқталды!',
          finishError: 'Қателерді анықтау кезеңін аяқтау мүмкін болмады.',
          downloadError: 'Файлды жүктеу қатесі',
          documentGenerated: '✅ Құжат сәтті жасалды!',
          documentGenerationError: 'Құжат генерациялау кезінде қате болды.',
          noFileUploaded: '❗ Құжат жүктелмеді',
          teachingLanguage: 'Оқу тілі',
          noPermission: 'Сізде бұл әрекетке құқық жоқ',
          // kz
tableHeaders: { category: 'Санат', grade: 'Сынып', status: 'Күйі', action: 'Әрекет' },


          summaryNotReady: 'Қорытынды жасалмады, 1 сағат күтіңіз',
        },
        ru: {
          loading: 'Загрузка...',
          noApplications: 'Заявки не найдены',
          view: 'Полный просмотр',
          back: 'Назад',
          deadline: 'Дедлайн',
          invalidApplication: 'Эта заявка сейчас не подлежит рассмотрению.',
          upload: 'Загрузить файл',
          finish: 'Завершить проверку ошибок',
          secondaryAction: 'Принятие решения',
          shortView: 'Подведение итогов',
          signErrorPrefix: '❌ Ошибка',
          errorGeneric: 'Произошла ошибка',
          generateReport: 'Сгенерировать документ',
          searchByGroup: 'Поиск по названию группы',
          signedSuccessfully: '✅ Подписано!',
          finalFilePrefix: 'заключение',
          fileUploadedDraft: 'Файл сохранен как черновик!',
          fileUploadError: 'Не удалось загрузить файл.',
          finishSuccess: 'Проверка ошибок завершена!',
          finishError: 'Не удалось завершить проверку ошибок.',
          downloadError: 'Ошибка загрузки файла',
          documentGenerated: '✅ Документ успешно создан!',
          documentGenerationError: 'Ошибка при генерации документа.',
          noFileUploaded: '❗ Файл не загружен',
          teachingLanguage: 'Язык обучения',
          noPermission: 'У вас нет прав для этого действия',
         // ru
tableHeaders: { category: 'Категория', grade: 'Класс', status: 'Статус', action: 'Действие' },
          summaryNotReady: 'Итог не сформирован, подождите 4 часа',
        },
      }[locale || 'kz']),
    [locale]
  );

  const DEADLINE_COL_TITLE = locale === 'ru' ? 'Дедлайн' : 'Дедлайн';

  // держим в URL актуальные page и q, чтобы возврат попадал на те же параметры
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(currentPage));
    const q = (groupFilter || '').trim();
    if (q) params.set('q', q);
    else params.delete('q');

    const nextSearch = `?${params.toString()}`;
    if (nextSearch !== location.search) {
      // replace, чтобы не плодить историю при каждом листании/фильтре
      navigate(`${location.pathname}${nextSearch}`, { replace: true });
    }
    // запомним «последний список»
    sessionStorage.setItem('ee_last_list', `${location.pathname}${nextSearch}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, groupFilter]);

  /* ========== Data fetching ========== */
  useEffect(() => {
    fetchApplications();
    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  fetchApplications(continuationFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [continuationFilter]);

  const fetchUserInfo = async () => {
    try {
      const res = await axios.get('/internal/profile', { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
      setUserIsScientist(!!res.data.academic_degree);
    } catch {

    }
  };

const fetchApplications = async (mode = continuationFilter) => {
  setLoading(true);
  try {
    const params = mode !== 'all' ? `?continuation_mode=${mode}` : '';
    const res = await axios.get(`/expertise/external/applications/in-expertise${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

      const raw =
        Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.applications)
          ? res.data.applications
          : [];

      const mapped = mapApiToGroups(raw, locale);
      const filtered = mapped
        .map((g) => ({ ...g, applications: g.applications.filter((a) => a.status_me !== 'finished') }))
        .filter((g) => g.applications.length > 0);

      setGroups(filtered);
      setExpanded((prev) => {
        const next = {};
        filtered.forEach((g) => (next[g.group_id] = prev[g.group_id] ?? false));
        return next;
      });
    } catch {

    } finally {
      setLoading(false);
    }
  };

  /* ========== Actions ========== */
  const navigateToApplication = (app) => {
    // запомним скролл списка перед уходом
    sessionStorage.setItem('ee_scroll_y', String(window.scrollY || 0));

    const status = String(app.status || '').toLowerCase();
    if (status === 'бағалау аяқталды') {
      setSelectedApplication(app);
      setApplicationViewMode('marking');
    } else if (status.includes('бағалау')) {
      navigate(`/final-decision/${app.id}?locale=${locale}`, {
        state: { from: location.pathname + location.search, locale },
      });
    } else if (status.includes('қателерді анықтау')) {
      const fromPath = `/staff-groups?tab=in-expertise#in-expertise`;
      sessionStorage.setItem('ee_last_list', fromPath);
navigate(`/error-review/${app.id}?from=${encodeURIComponent(fromPath)}&locale=${locale}`, {
  state: { from: fromPath, locale, activeTab: 'in-expertise' },
});


    } else {
      alert(t.invalidApplication);
    }
  };

  const downloadFinalDecision = async (applicationId) => {
    try {
      const res = await axios.get(`/external-expert/applications/${applicationId}/download-report`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${t.finalFilePrefix}_${applicationId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      alert(t.downloadError);
    }
  };

  const generateFinalDecision = async (applicationId) => {
    try {
      await axios.post(
        `/external-expert/applications/${applicationId}/generate-report`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(t.documentGenerated);
    } catch {
      alert(t.documentGenerationError);
    }
  };

  const fetchErrorReview = async (applicationId) => {
    try {
      const res = await axios.get(`expertise/external/applications/${applicationId}/error-review`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setErrorReviewData(res.data);
    } catch {
      alert(t.errorGeneric);
    }
  };

  const handleUploadErrorFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`/expertise/external/applications/${selectedApplication.id}/error-file`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      alert(t.fileUploadedDraft);
    } catch {
      alert(t.fileUploadError);
    }
  };

  const handleFinishErrorReview = async () => {
    try {
      await axios.post(
        `/expertise/external/applications/${selectedApplication.id}/error-review/finish`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert(t.finishSuccess);
      setSelectedApplication(null);
      setApplicationViewMode(null);
      fetchApplications();
    } catch {
      alert(t.finishError);
    }
  };

  const openApplication = async (app) => {
  try {
    const docRes = await axios.get(`/final-documents/by-application/${app.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // если документ ещё не создан — защищённая проверка
    const docId = docRes?.data?.document_id;
    if (!docId) {
      alert(t.summaryNotReady);
      return;
    }

    const statusRes = await axios.get(`/final-documents/${docId}/signatures/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const pdfRes = await axios.get(`/final-documents/${docId}/base64`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setBase64File(pdfRes.data.base64);

    setSelectedApplication({
      ...app,
      document_id: docId,
      title: docRes.data.application_data.title,
      category: docRes.data.application_data.category,
      authors: docRes.data.application_data.authors,
      publisher: docRes.data.application_data.publisher,
      grade: docRes.data.application_data.grade,
    });

    setFinalAverage(docRes.data.average_score);
    setSummaryDecision(docRes.data.verdict);
    setSignatures(docRes.data.signers || []);
    setPendingSignatures(statusRes.data.not_signed || []);
    setShowSignatureModal(true);
  } catch (err) {
    const msg = String(err?.response?.data?.message || '').toLowerCase();
    const code = err?.response?.status;

    // типичные коды/сообщения когда ещё не сгенерировано
    if (code === 404 || code === 409 || msg.includes('not generated') || msg.includes('not ready')) {
      alert(t.summaryNotReady);
    } else {
      alert(t.errorGeneric);
    }
  }
};


  const handleSecondaryAction = (app) => {
    setSelectedApplication(app);
    setShowSignatureModal(false);
  };

  const handleSign = async () => {
    if (!base64File) {
      alert(t.noFileUploaded);
      return;
    }
    window.blockScreen?.();
    window.createCAdESFromBase64('PKCS12', 'SIGNATURE', base64File, false, async (result) => {
      window.unblockScreen?.();
      if (result.code !== '200') {
        alert(`${t.signErrorPrefix}: ${result.message}`);
        return;
      }
      try {
        await axios.post(
          `/final-documents/sign/${selectedApplication.document_id}`,
          { signed_pdf_base64: result.responseObject, filename: `signed_doc_${selectedApplication.document_id}_${user?.id}.pdf` },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert(t.signedSuccessfully);
        setSelectedApplication(null);
        fetchApplications();
      } catch (err) {
        alert(err.response?.data?.message || t.errorGeneric);
      }
    });
  };

  /* ========== Derived UI state ========== */
  const filteredGroups = groups.filter((g) =>
    (g.group_title || '').toLowerCase().includes(groupFilter.toLowerCase())
  );

  const totalPages = Math.ceil((filteredGroups.length || 1) / itemsPerPage) || 1;
  const currentSafe = Math.min(Math.max(1, currentPage), totalPages);
  const paginatedGroups = filteredGroups.slice(
    (currentSafe - 1) * itemsPerPage,
    currentSafe * itemsPerPage
  );

  const anyOpen = useMemo(
    () => paginatedGroups.some((g) => expanded[g.group_id]),
    [paginatedGroups, expanded]
  );

  const toggleAllGroups = useCallback(() => {
    setExpanded((prev) => {
      const target = !anyOpen;
      const next = { ...prev };
      paginatedGroups.forEach((g) => {
        next[g.group_id] = target;
      });
      return next;
    });
  }, [anyOpen, paginatedGroups]);

  /* ========== Render ========== */
  if (loading) return <p className="gai-muted">{t.loading}</p>;

  if (selectedApplication && applicationViewMode === 'errorReview') {
    return (
      <div className="gai-root">
        <button
          onClick={() => {
            setSelectedApplication(null);
            setApplicationViewMode(null);
          }}
          className="gai-btn"
        >
          ⬅ {t.back}
        </button>

        <h2 className="gai-experts-title">{selectedApplication.title}</h2>

        {errorReviewData && (
          <div>
            <p>
              <strong>Subject:</strong> {errorReviewData.subject}
            </p>
            <p>
              <strong>Grade:</strong> {errorReviewData.grade}
            </p>
            <p>
              <strong>{t.teachingLanguage}:</strong> {errorReviewData.teaching_language}
            </p>

            <h4>📄 Файлдар:</h4>
            <ul>
              {(errorReviewData.files || []).map((file, idx) => (
                <li key={idx}>
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.section} — {file.label}
                  </a>
                </li>
              ))}
            </ul>

            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <input type="file" onChange={handleUploadErrorFile} />
              <button className="gai-btn-primary" onClick={handleFinishErrorReview}>
                {t.finish}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (selectedApplication && applicationViewMode === 'marking') {
    return (
      <ExternalExpertScoringPage
        locale={locale}
        applicationId={selectedApplication?.id}
        onBack={() => {
          setSelectedApplication(null);
          setApplicationViewMode(null);
          fetchApplications();
        }}
      />
    );
  }

  return (
    <div className="gai-root">
      {/* Фильтр */}
      {/* Фильтр + поиск */}
<div className="gai-filter" style={{ marginBottom: 10 }}>
  <input
    type="text"
    className="gai-input"
    placeholder={t.searchByGroup}
    value={groupFilter}
    onChange={(e) => setGroupFilter(e.target.value)}
    style={{ maxWidth: 320 }}
  />
</div>

{/* ← ДОБАВЬ ЭТО: кнопки continuation_mode */}
<div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
  {[
    { key: 'all',          labelKz: 'Барлығы',   labelRu: 'Все' },
    { key: 'new',          labelKz: 'Жаңа',      labelRu: 'Новые' },
    { key: 'continuation', labelKz: 'Доработка', labelRu: 'Доработки' },
  ].map(({ key, labelKz, labelRu }) => (
    <button
      key={key}
      onClick={() => { setContinuationFilter(key); setCurrentPage(1); }}
      style={{
        padding: '7px 16px',
        borderRadius: 20,
        border: continuationFilter === key
          ? '2px solid #2563eb'
          : '1.5px solid rgba(15,23,42,0.15)',
        background: continuationFilter === key ? '#2563eb' : '#fff',
        color: continuationFilter === key ? '#fff' : '#334155',
        fontWeight: continuationFilter === key ? 700 : 500,
        fontSize: 13,
        cursor: 'pointer',
        transition: 'all .15s ease',
      }}
    >
      {locale === 'ru' ? labelRu : labelKz}
    </button>
  ))}
</div>

      {/* Toolbar — кнопка "развернуть/свернуть все" */}
      <div className="gai-toolbar">
        <button className="gai-btn" onClick={toggleAllGroups}>
          {anyOpen ? (locale === 'ru' ? 'Свернуть все' : 'Барлығын жабу') : locale === 'ru' ? 'Развернуть все' : 'Барлығын ашу'}
        </button>
      </div>

      {/* Группы */}
      {paginatedGroups.length === 0 && <p className="gai-muted">{t.noApplications}</p>}

      {paginatedGroups.map((group) => {
        const isOpen = !!expanded[group.group_id];
        const panelId = `panel-${group.group_id}`;
        const headerId = `header-${group.group_id}`;

        const onHeaderKeyDown = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleGroup(group.group_id);
          }
        };

        return (
          <div key={group.group_id || 'no-group'} className={`gai-group-section ${isOpen ? 'is-open' : 'is-closed'}`}>
            <div
              id={headerId}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggleGroup(group.group_id)}
              onKeyDown={onHeaderKeyDown}
              className="gai-head"
            >
              <span className="gai-chev">{isOpen ? '▾' : '▸'}</span>
              <span className="gai-group-title">{group.group_title}</span>

              {group.applications.some(a => a.continuation_mode === 'continuation') && (
    <span style={{
      marginLeft: 10,
      fontSize: 11, fontWeight: 700,
      background: '#fef3c7', color: '#92400e',
      border: '1px solid #fcd34d',
      borderRadius: 999, padding: '2px 10px',
      flexShrink: 0,
    }}>
      {locale === 'ru' ? '↩ Доработка' : '↩ Доработка'}
    </span>
  )}
            </div>

            {isOpen && (
              <div id={panelId} role="region" aria-labelledby={headerId} className="gai-group-panel">
                <table className="gai-table">
                  <thead>
                    <tr>
                      <th>{t.tableHeaders.category}</th>
                      <th>{DEADLINE_COL_TITLE}</th>
                      <th>{t.tableHeaders.status}</th>
                      <th>{t.tableHeaders.action}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {group.applications.map((app) => {
                      const sev = getTimeSeverity(app._deadline_raw);
                      const deadlineText = app.deadline_text;

                      return (
                        <React.Fragment key={app.id}>
                          {/* строка с прогрессом */}
                          <tr>
                            <td colSpan={5} className="eei-progress-cell">
                              <InlineStatusProgressBar status={app.status_me} locale={locale} />
                            </td>
                          </tr>

                          {/* основная строка */}
                          <tr>
                             <td>
  {app.category}
  {app.continuation_mode === 'continuation' && (
    <span style={{
      marginLeft: 6, fontSize: 10, fontWeight: 700,
      background: '#fef3c7', color: '#92400e',
      border: '1px solid #fcd34d',
      borderRadius: 999, padding: '1px 7px',
    }}>
      {locale === 'ru' ? 'Доработка' : 'Доработка'}
    </span>
  )}
</td>

                            {/* колонка Дедлайн */}
                            <td>
                              {deadlineText !== '—' ? (
                                <span
                                  className={`gai-deadline ${sev} ${
                                    /Мерзімі өтті|Просрочено/i.test(deadlineText) ? 'overdue' : ''
                                  }`}
                                >
                                  ⏳ {deadlineText}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>

                           <td>{(STATUS_LABELS[locale] || STATUS_LABELS.kz)[app.status_me]}</td>

                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
  {/* Всегда можно открыть просмотр */}
  <button
    className="gai-btn-link"
    onClick={() => navigateToApplication(app)}
  >
    <i className="gai-ico-eye" /> {t.view}
  </button>

  {(() => {
    const curIdx = STATUS_STEPS.indexOf(app.status_me);
    const showDecision = curIdx >= STATUS_STEPS.indexOf('decision'); // с "decision" и дальше
    const showSummary  = curIdx >= STATUS_STEPS.indexOf('summary');  // с "summary" и дальше

    return (
      <>
        {showDecision && (
          <button
            className="gai-btn-approve-group"
            style={{ marginLeft: 8 }}
            onClick={() => handleSecondaryAction(app)}
          >
            {t.secondaryAction} {/* Шешім шығару */}
          </button>
        )}

        {showSummary && (
          <button
            className="gai-btn"
            style={{ marginLeft: 8 }}
            onClick={() => openApplication(app)}
          >
            {t.shortView} {/* Қорытындылау */}
          </button>
        )}
      </>
    );
  })()}
</td>


                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            </div>
        );
      })}

      {selectedApplication && showSignatureModal && (
        <SignatureApplicationModal
          application={selectedApplication}
          onClose={() => {
            setSelectedApplication(null);
            setShowSignatureModal(false);
          }}
          locale={locale}
          finalAverage={finalAverage}
          summaryDecision={summaryDecision}
          t={t}
          downloadFinalDecision={downloadFinalDecision}
          showSignatures={true}
          setShowSignatures={() => {}}
          signatures={signatures}
          userId={user?.id}
          pendingSignatures={pendingSignatures}
          handleSign={handleSign}
          token={token}
          refreshApplication={async () => {
            await openApplication(selectedApplication);
          }}
          onSignedSuccess={() => {
            fetchApplications();
          }}
         onSignedFail={(msg) => alert(msg)}
        />
      )}

{selectedApplication && !showSignatureModal && (
  <ApplicationDetailsModal
    selectedApplication={selectedApplication}
    setSelectedApplication={setSelectedApplication}
    downloadDecisionFile={(id) => downloadFinalDecision(id)}
    generateFinalDecision={userIsScientist ? generateFinalDecision : () => alert(t.noPermission)}
    downloadFinalDecision={(id) => downloadFinalDecision(id)}
    setShowUploadModal={() => {}}
    token={localStorage.getItem('token')}
    t={t}
    locale={locale}
    continuationMode={selectedApplication.continuation_mode ?? 'new'}  
  />
)}


      {/* Пейджер */}
      <div className="gai-pager pager-scope">
        <button
          className="gai-pager-btn"
          aria-label="Previous page"
          disabled={currentSafe === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          ‹
        </button>

        <span className="gai-counter">
          {currentSafe} / {totalPages}
        </span>

        <button
          className="gai-pager-btn"
          aria-label="Next page"
          disabled={currentSafe === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          ›
        </button>
      </div>
    </div>
  );
}
